import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Modal from "@material-ui/core/Modal";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import Api from "lib/api/httpRequestHelper";
import storageFnc from "lib/localStorage";
import { Backdrop, Fade } from "@material-ui/core";
import UtilsFncs from "utils/UtilsFncs";

const INACTIVITY_TIMEOUT_DURATION_MS = 10 * 60 * 1000;
const WARNING_MODAL_COUNTDOWN_SECONDS = 60;

const useStyles = makeStyles((theme) => ({
  buttonContainer: {
    marginTop: theme.spacing(2),
  },
}));

function InactivityTimeout() {
  const classes = useStyles();
  const history = useHistory();

  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_MODAL_COUNTDOWN_SECONDS);
  const [timeUntilModalVisible, setTimeUntilModalVisible] = useState(
    INACTIVITY_TIMEOUT_DURATION_MS / 1000
  );

  const inactivityTimerRef = useRef(null);
  const warningCountdownTimerRef = useRef(null);
  const debugLogTimerRef = useRef(null);

  const performLogout = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningCountdownTimerRef.current)
      clearInterval(warningCountdownTimerRef.current);
    if (debugLogTimerRef.current) clearInterval(debugLogTimerRef.current);

    setIsWarningModalOpen(false);

    storageFnc.clear();

    localStorage.setItem(
      "sessionExpiredMessage",
      "Sua sessão foi encerrada por inatividade."
    );
    localStorage.setItem("sessionExpired", "true");
    UtilsFncs.RedirectToLoginPage();
  }, [history]);

  const showWarningModal = useCallback(() => {
    setIsWarningModalOpen(true);
    setCountdown(WARNING_MODAL_COUNTDOWN_SECONDS);

    if (warningCountdownTimerRef.current)
      clearInterval(warningCountdownTimerRef.current);

    warningCountdownTimerRef.current = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(warningCountdownTimerRef.current);
          performLogout();
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);
  }, [performLogout]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningCountdownTimerRef.current)
      clearInterval(warningCountdownTimerRef.current);
    if (debugLogTimerRef.current) clearInterval(debugLogTimerRef.current);

    if (isWarningModalOpen) {
      setIsWarningModalOpen(false);
    }

    inactivityTimerRef.current = setTimeout(
      showWarningModal,
      INACTIVITY_TIMEOUT_DURATION_MS
    );

    setTimeUntilModalVisible(INACTIVITY_TIMEOUT_DURATION_MS / 1000);
  }, [showWarningModal]);

  const handleStillHere = async () => {
    setIsWarningModalOpen(false);
    if (warningCountdownTimerRef.current)
      clearInterval(warningCountdownTimerRef.current);

    resetInactivityTimer();

    try {
      await Api.refreshToken();
      console.log("Session token refreshed.");
    } catch (error) {
      console.error("Failed to refresh session token:", error);
      performLogout();
    }
  };

  useEffect(() => {
    resetInactivityTimer();

    const activityEvents = ["mousemove", "keydown", "scroll", "click"];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningCountdownTimerRef.current)
        clearInterval(warningCountdownTimerRef.current);
      if (debugLogTimerRef.current) clearInterval(debugLogTimerRef.current);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [resetInactivityTimer]);

  if (!isWarningModalOpen) {
    return null;
  }

  return (
    <Modal
      open={isWarningModalOpen}
      aria-labelledby="inactivity-warning-modal-title"
      aria-describedby="inactivity-warning-modal-description"
      BackdropComponent={Backdrop}
      onClose={() => handleStillHere()}
      style={{
        display: "flex",
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Fade in={isWarningModalOpen}>
        <div
          className={classes.paper}
          style={{
            backgroundColor: "#FFF",
            padding: "20px",
            borderRadius: 8,
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography
              id="inactivity-warning-modal-title"
              variant="h6"
              component="h2"
            >
              Aviso de Inatividade
            </Typography>
            <Typography
              id="inactivity-warning-modal-description"
              sx={{ mt: 2 }}
            >
              Sua sessão está prestes a expirar por inatividade.
            </Typography>
            <Typography variant="h4" component="p" sx={{ mt: 2, mb: 2 }}>
              0:{countdown}
            </Typography>
            <Box className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleStillHere}
              >
                Ainda estou aqui
              </Button>
            </Box>
          </Box>
        </div>
      </Fade>
    </Modal>
  );
}

export default InactivityTimeout;
